/**
 * SOVRANOVA — ReflectionThread
 *
 * The part that turns a reflection from a receipt into a conversation.
 *
 * Before this, Reflect answered once and stopped. You couldn't ask why it said
 * what it said, go back into a moment, or work out what to actually do —
 * you had to leave for the AI Session screen and start again from a blank page.
 * Now the conversation happens where the reflection is, in voice, and the whole
 * thread is kept on the reflection so you can pick it up in three weeks.
 *
 * Used in two places on purpose: under a reflection you just did, and under an
 * old one you've reopened. Same component, same thread, same voice.
 *
 * Your speech is transcribed and the audio is destroyed (services/voiceTurn).
 * The companion's replies are spoken in full (services/speech).
 */
import { useState, useRef, useEffect, useCallback } from 'react'
import { api } from '../../services/api'
import { beginCapture, micSupported } from '../../services/voiceTurn'
import { speakLong } from '../../services/speech'
import { stopAllAudio } from '../../services/audioBus'
import { useChatAutoScroll } from '../../hooks/useChatAutoScroll'

export function ReflectionThread({ insightId, initialTurns = [], voiceOff = false, autoSpeak = true }) {
  const [turns,   setTurns]   = useState(() => (Array.isArray(initialTurns) ? initialTurns : []))
  const [draft,   setDraft]   = useState('')
  const [busy,    setBusy]    = useState(false)
  const [rec,     setRec]     = useState(false)
  const [writing, setWriting] = useState(false)   // transcribing
  const [speaking, setSpeaking] = useState(false)
  const [err,     setErr]     = useState(null)
  const [crisis,  setCrisis]  = useState(null)
  const [unsaved, setUnsaved] = useState(false)   // migration not applied yet

  const captureRef = useRef(null)
  const ttsRef     = useRef(null)
  const tokenRef   = useRef(0)
  const endRef     = useRef(null)

  // A thread loaded from a different reflection must replace this one, not
  // append to it — reopening reflection B should never show B's answers under
  // A's conversation.
  useEffect(() => {
    setTurns(Array.isArray(initialTurns) ? initialTurns : [])
    setDraft(''); setErr(null); setCrisis(null)
  }, [insightId])   // eslint-disable-line react-hooks/exhaustive-deps

  useChatAutoScroll(endRef, turns)   // follow only while the reader is at the bottom

  const hush = useCallback(() => {
    tokenRef.current++
    try { ttsRef.current?.pause() } catch { /* noop */ }
    setSpeaking(false)
  }, [])

  // Never keep talking, or holding the microphone, after this unmounts.
  useEffect(() => () => {
    tokenRef.current++
    try { ttsRef.current?.pause() } catch {}
    try { captureRef.current?.cancel() } catch {}
  }, [])
  useEffect(() => { if (voiceOff) hush() }, [voiceOff, hush])

  const say = useCallback(async (text) => {
    if (!text || voiceOff) return
    stopAllAudio()                       // this reply owns the room now
    const token = ++tokenRef.current
    setSpeaking(true)
    try { await speakLong(text, ttsRef, () => tokenRef.current !== token) }
    catch { /* the words are on screen regardless */ }
    if (tokenRef.current === token) setSpeaking(false)
  }, [voiceOff])

  const send = async (text) => {
    const body = String(text || '').trim()
    if (!body || busy) return
    if (!insightId) {
      setErr("This reflection hasn't finished saving yet — give it a moment and try again.")
      return
    }
    hush()
    setErr(null); setCrisis(null); setDraft('')
    setTurns(prev => [...prev, { role: 'user', content: body, at: new Date().toISOString() }])
    setBusy(true)
    try {
      const r = await api.post('/ambient/converse', { insightId, message: body })
      if (r?.crisis) {
        setCrisis({ text: r.reply, resources: r.resources || null })
      } else if (r?.reply) {
        setTurns(prev => [...prev, { role: 'assistant', content: r.reply, at: new Date().toISOString() }])
        setUnsaved(r.persisted === false)
        if (autoSpeak) say(r.reply)
      }
    } catch (e) {
      setErr("I couldn't get that through just now — your words are still here, try again in a moment.")
    } finally {
      setBusy(false)
    }
  }

  // ── Voice in: capture, transcribe, destroy the audio, send ────────────────
  const startRec = async () => {
    setErr(null)
    hush()
    try {
      captureRef.current = await beginCapture()
      setRec(true)
    } catch (e) {
      captureRef.current = null
      setErr(e?.name === 'NotAllowedError'
        ? 'Microphone permission was denied — you can still type.'
        : 'Could not open the microphone.')
    }
  }

  const stopRec = async () => {
    const cap = captureRef.current
    captureRef.current = null
    setRec(false)
    if (!cap) return
    setWriting(true)
    try {
      const text = await cap.stop()
      if (text) await send(text)
    } catch {
      setErr("Couldn't catch that one — try again, or type it instead.")
    } finally {
      setWriting(false)
    }
  }

  const disabled = busy || writing || rec

  return (
    <div style={s.wrap}>
      {turns.length > 0 && (
        <div style={s.turns}>
          {turns.map((t, i) => (
            <div key={i} style={t.role === 'assistant' ? s.them : s.you}>
              <span style={s.who}>{t.role === 'assistant' ? 'SOVRANOVA' : 'You'}</span>
              <p style={s.body}>{t.content}</p>
              {t.role === 'assistant' && !voiceOff && (
                <button style={s.hear} onClick={() => (speaking ? hush() : say(t.content))}>
                  {speaking ? '■ Stop' : '🔊 Hear this'}
                </button>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>
      )}

      {busy && <p style={s.thinking}>Thinking with you…</p>}

      {/* Crisis replies are shown, never spoken and never folded into the
          thread — they may need to be read, kept private, and acted on. */}
      {crisis && (
        <div style={s.crisis}>
          <p style={s.crisisText}>{crisis.text}</p>
          {crisis.resources && (
            <div style={s.resources}>
              {['primary','text','youth','suicide','emergency'].map(k => crisis.resources[k] && (
                <div key={k} style={s.resource}>{crisis.resources[k]}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <div style={s.composer}>
        {micSupported() && (
          <button
            style={{ ...s.mic, ...(rec ? s.micOn : {}) }}
            onClick={rec ? stopRec : startRec}
            disabled={busy || writing}
            title={rec ? 'Stop and send' : 'Speak'}>
            {writing ? '⏳' : rec ? '■' : '🎙'}
          </button>
        )}
        <textarea
          style={s.input}
          rows={2}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(draft) } }}
          placeholder={rec ? 'Listening…' : writing ? 'Writing that down…' : 'Ask about this, or keep going…'}
          disabled={disabled}
        />
        <button style={s.send} onClick={() => send(draft)} disabled={disabled || !draft.trim()}>↑</button>
      </div>

      {err && <p style={s.err}>{err}</p>}
      {unsaved && (
        <p style={s.warn}>
          We're talking, but this thread isn't being saved yet — the database needs migration_014 applied.
        </p>
      )}
    </div>
  )
}

const s = {
  wrap:     { marginTop:'1rem', paddingTop:'0.9rem', borderTop:'1px solid rgba(255,255,255,.09)', display:'flex', flexDirection:'column', gap:'0.6rem' },
  turns:    { display:'flex', flexDirection:'column', gap:'0.75rem', maxHeight:'46vh', overflowY:'auto' },
  you:      { alignSelf:'flex-end', maxWidth:'88%', background:'rgba(255,255,255,.07)', border:'1px solid rgba(255,255,255,.1)', borderRadius:'0.9rem 0.9rem 0.2rem 0.9rem', padding:'0.6rem 0.8rem' },
  them:     { alignSelf:'flex-start', maxWidth:'92%', background:'rgba(139,92,246,.11)', border:'1px solid rgba(139,92,246,.26)', borderRadius:'0.9rem 0.9rem 0.9rem 0.2rem', padding:'0.65rem 0.85rem' },
  who:      { display:'block', fontSize:'0.62rem', letterSpacing:'0.1em', textTransform:'uppercase', fontWeight:700, color:'rgba(240,244,248,.42)', marginBottom:'0.25rem' },
  body:     { margin:0, fontSize:'0.94rem', lineHeight:1.62, color:'#f2f0fb', whiteSpace:'pre-line' },
  hear:     { marginTop:'0.45rem', background:'transparent', border:'1px solid rgba(255,255,255,.16)', color:'rgba(240,244,248,.7)', fontSize:'0.72rem', borderRadius:'1rem', padding:'0.22rem 0.6rem', cursor:'pointer', fontFamily:'inherit' },
  thinking: { margin:0, fontSize:'0.8rem', color:'rgba(240,244,248,.45)', fontStyle:'italic' },
  composer: { display:'flex', alignItems:'flex-end', gap:'0.5rem' },
  mic:      { flexShrink:0, width:42, height:42, borderRadius:'50%', border:'1px solid rgba(139,92,246,.4)', background:'rgba(139,92,246,.15)', color:'#d9ccff', fontSize:'1rem', cursor:'pointer' },
  micOn:    { background:'rgba(239,68,68,.22)', border:'1px solid rgba(239,68,68,.55)', color:'#ffd9d9' },
  input:    { flex:1, minWidth:0, boxSizing:'border-box', background:'rgba(0,0,0,.25)', border:'1px solid rgba(255,255,255,.12)', borderRadius:'0.7rem', color:'#f0f4f8', fontSize:'0.92rem', lineHeight:1.5, padding:'0.6rem 0.7rem', resize:'vertical', fontFamily:'inherit' },
  send:     { flexShrink:0, width:42, height:42, borderRadius:'50%', border:'none', background:'#8b5cf6', color:'#fff', fontSize:'1.1rem', fontWeight:700, cursor:'pointer' },
  err:      { margin:0, fontSize:'0.8rem', color:'#ffb4b4' },
  warn:     { margin:0, fontSize:'0.74rem', color:'#f2c98a', lineHeight:1.5 },
  crisis:   { background:'rgba(239,68,68,.12)', border:'1px solid rgba(239,68,68,.4)', borderRadius:'0.9rem', padding:'0.9rem 1rem' },
  crisisText:{ margin:'0 0 0.6rem', fontSize:'0.93rem', lineHeight:1.65, color:'#ffe6e6', whiteSpace:'pre-line' },
  resources:{ display:'flex', flexDirection:'column', gap:'0.35rem' },
  resource: { fontSize:'0.85rem', color:'#ffd9d9' },
}
